import { useEffect, useId, useMemo, useState } from "react";
import { FileDropZone } from "@/components/shared/FileDropZone";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { Button } from "@/components/ui/button";
import { ClientLogo } from "./ClientLogo";

/** The pictures a logo may be; the server judges the bytes, this only narrows the picker. */
const LOGO_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
};

const LOGO_KINDS = "PNG, JPEG or WebP";

/** A file the attorney chose, as a preview the browser can draw before anything is stored. */
export function usePreview(file: File | null): string | null {
  const url = useMemo(
    () => (file === null ? null : URL.createObjectURL(file)),
    [file],
  );
  // Released when the file is replaced or the form goes, so a preview holds no
  // memory past the form it was drawn in.
  useEffect(
    () => () => {
      if (url !== null) URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}

/**
 * The optional logo in a client's form: the house drop zone while there is no
 * logo, and once there is one, the picture as it will be and a way to take it
 * off, which brings the zone back. A logo is replaced by taking it off first.
 *
 * The zone holds one file, and says so at once when a file is not a picture a
 * logo may be, rather than leaving it to the server after the client is saved.
 */
export function LogoField({
  name,
  shownUrl,
  onChoose,
  onRemove,
}: {
  /** The client's name, for the initials drawn while there is no picture. */
  name: string;
  /** What the card would show: a chosen file's preview, or the stored logo's link. */
  shownUrl: string | null;
  onChoose: (file: File) => void;
  onRemove: () => void;
}) {
  const inputId = useId();
  const [refused, setRefused] = useState(false);
  const chosen = shownUrl !== null;
  return (
    <div>
      <FieldLabel
        htmlFor={chosen ? undefined : inputId}
        look="sentence"
        optional
      >
        Company logo
      </FieldLabel>
      {chosen ? (
        <div className="flex items-center gap-[11px]">
          <ClientLogo
            name={name}
            logoUrl={shownUrl}
            className="size-[54px] shrink-0 rounded-md text-base"
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onRemove}
            className="text-muted-foreground hover:border-destructive-border hover:text-destructive-strong"
          >
            Remove
          </Button>
        </div>
      ) : (
        <FileDropZone
          inputId={inputId}
          size="compact"
          multiple={false}
          accept={LOGO_ACCEPT}
          title="Drop a logo here, or click to choose one"
          hint={LOGO_KINDS}
          onDrop={(files) => {
            setRefused(false);
            if (files.length > 0) onChoose(files[0]);
          }}
          onRejected={() => {
            setRefused(true);
          }}
        />
      )}
      {refused && (
        <p role="alert" className="mt-1.5 text-[12px] text-destructive-strong">
          That file is not a {LOGO_KINDS} image.
        </p>
      )}
    </div>
  );
}
